// @ts-check
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Retorna un array con el id como Number y String para queries tolerantes
 * a tipos mixtos en Firestore. Usar con where("field", "in", tolerantId(value))
 *
 * @param {string | number} id - ID a convertir
 * @returns {Array<string | number>} Array con variantes numéricas y string del ID
 */
export const tolerantId = (id) => {
  const num = Number(id);
  const str = String(id);
  if (isNaN(num)) return [str];
  return [num, str];
};

/**
 * Edificio en la forma en que el servidor lo guarda en users.accessBids
 * (normBid de functions/lib/access.js): el string del buildingid numérico,
 * sin ceros a la izquierda. 10, "10" y " 010 " dan "10". Cualquier otra cosa
 * (vacío, null, letras, negativos, decimales) da null.
 *
 * @param {unknown} buildingId - Edificio
 * @returns {string | null}
 */
export function bidParaAccessBids(buildingId) {
  if (buildingId == null || typeof buildingId === "boolean" || typeof buildingId === "object") return null;
  let s;
  if (typeof buildingId === "number") {
    if (!Number.isFinite(buildingId)) return null;
    s = String(buildingId);
  } else {
    s = String(buildingId).trim();
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      if (Number.isSafeInteger(n)) s = String(n);
    }
  }
  return /^\d+$/.test(s) ? s : null;
}

// Reglas 0D (C10): el staff lee una ficha ajena de `users` solo si su
// accessBids incluye un edificio que administra, y una consulta de lista pasa
// solo si lo prueba con where("accessBids", "array-contains", bid). Una consulta
// que no lo fija (userid in, celular in, email ==) se niega ENTERA.
//
// Tope por consulta: array-contains × in = 1 × 30 disyunciones. Cada userid
// entra con sus variantes number/string (tolerantId), así que van 15 userids
// por consulta; por otro campo (celular, email), 30 valores.
const USERIDS_POR_CONSULTA = 15;
const VALORES_POR_CONSULTA = 30;

/**
 * Filtro C10 de las consultas de fichas de un edificio. Sin un edificio válido
 * lanza: una consulta sin accessBids se niega entera y no hay forma "sin
 * edificio" de leer fichas ajenas.
 *
 * @param {unknown} buildingId - Edificio desde el que se mira
 * @param {string} quien - Nombre de la función, para el mensaje
 */
function filtroDelEdificio(buildingId, quien) {
  const bid = bidParaAccessBids(buildingId);
  if (!bid) {
    throw new Error(
      `${quien}: falta el edificio. Con las reglas 0D las fichas de users se leen por edificio (accessBids).`
    );
  }
  return where("accessBids", "array-contains", bid);
}

/** Valores sin repetir (number y string son distintos: 7 y "7" van los dos). */
const sinRepetir = (valores) => [...new Map(valores.map((v) => [`${typeof v}:${v}`, v])).values()];

/** Parte una lista en tramos de `n`. */
const enTandas = (lista, n) => {
  const tandas = [];
  for (let i = 0; i < lista.length; i += n) tandas.push(lista.slice(i, i + n));
  return tandas;
};

// Si un userid tiene varias fichas (la de residente y la de staff de la misma
// persona, o una archivada o fusionada), gana la de residente vigente: es la
// que miran Cobranza, el cierre, la liquidación y Cuentas.
const esResidente = (d) => d.typeuser == null || d.typeuser === "" || Number(d.typeuser) === 0;
const esVigente = (d) => !d.archivedAt && d.mergedIntoUserid == null;
const rangoDeFicha = (d) => (esResidente(d) ? 0 : 2) + (esVigente(d) ? 0 : 1);

/**
 * Fichas `users` de un edificio por `userid`, tolerando number/string.
 *
 * Consulta (C10): where("accessBids", "array-contains", bid canónico) +
 * where("userid", "in", lote), en lotes de hasta 15 userids (≤ 30 valores con
 * sus variantes). Devuelve una ficha por userid (la de residente vigente si hay
 * varias) con `id` = id del documento y, si faltan, name "Sin nombre", email
 * "Sin email" y typeuser 0. Un userid cuya ficha no tiene el edificio en
 * accessBids no aparece.
 *
 * @param {Array<string | number | null | undefined>} rawIds - Lista de user IDs a buscar
 * @param {string | number | null | undefined} buildingId - Edificio desde el que se mira (obligatorio)
 * @returns {Promise<Array<import("../types/firestore").User & Record<string, any>>>} Usuarios encontrados
 * @throws {Error} sin un edificio válido; y si la consulta falla (p. ej. permission-denied)
 */
export async function fetchUsersByUserIds(rawIds, buildingId) {
  const delEdificio = filtroDelEdificio(buildingId, "fetchUsersByUserIds");
  const unique = [
    ...new Set(
      (Array.isArray(rawIds) ? rawIds : [])
        .filter((x) => x != null && x !== "")
        .map((x) => String(x).trim())
        .filter(Boolean)
    ),
  ];
  if (unique.length === 0) return [];

  const lotes = enTandas(unique, USERIDS_POR_CONSULTA).map((ids) => sinRepetir(ids.flatMap((id) => tolerantId(id))));
  const snaps = await Promise.all(
    lotes.map((valores) => getDocs(query(collection(db, "users"), delEdificio, where("userid", "in", valores))))
  );

  const byKey = new Map();
  snaps.forEach((snap) => {
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      const key = String(data.userid ?? d.id);
      const actual = byKey.get(key);
      if (actual && rangoDeFicha(actual) <= rangoDeFicha(data)) return;
      byKey.set(key, {
        userid: data.userid ?? d.id,
        name: data.name || "Sin nombre",
        email: data.email || "Sin email",
        typeuser: data.typeuser ?? 0,
        ...data,
        // El id del documento siempre gana sobre un campo `id` guardado en la ficha.
        id: d.id,
      });
    });
  });
  return Array.from(byKey.values());
}

/**
 * Fichas `users` de un edificio por otro campo (celular, email…), con la misma
 * forma de consulta que fetchUsersByUserIds: where("accessBids",
 * "array-contains", bid canónico) + where(campo, "in", lote), en lotes de 30
 * valores. Los valores se buscan tal cual (el llamador pasa las variantes que
 * quiera). Devuelve TODAS las fichas que coinciden, una por documento y sin
 * valores por defecto: { ...datos, id }.
 *
 * @param {string} field - Campo de users (p. ej. "celular", "email")
 * @param {Array<string | number | null | undefined> | string | number} values - Valores a buscar
 * @param {string | number | null | undefined} buildingId - Edificio desde el que se mira (obligatorio)
 * @returns {Promise<Array<Record<string, any> & { id: string }>>}
 * @throws {Error} sin un edificio o un campo válidos; y si la consulta falla
 */
export async function fetchUsersByField(field, values, buildingId) {
  const delEdificio = filtroDelEdificio(buildingId, "fetchUsersByField");
  if (typeof field !== "string" || !field.trim()) {
    throw new Error("fetchUsersByField: falta el campo a buscar.");
  }
  const lista = Array.isArray(values) ? values : [values];
  const unicos = sinRepetir(lista.filter((v) => v != null && v !== ""));
  if (unicos.length === 0) return [];

  const snaps = await Promise.all(
    enTandas(unicos, VALORES_POR_CONSULTA).map((lote) =>
      getDocs(query(collection(db, "users"), delEdificio, where(field, "in", lote)))
    )
  );
  const porId = new Map();
  snaps.forEach((snap) => {
    snap.docs.forEach((d) => {
      if (!porId.has(d.id)) porId.set(d.id, { ...(d.data() || {}), id: d.id });
    });
  });
  return [...porId.values()];
}

/**
 * Userids vinculados a un edificio: apartamentos (owner/tenant) + userbuilding.
 * FIN-04 / SEC-06: evita cargar toda la colección `users`.
 *
 * @param {string | number | null | undefined} buildingId - ID del edificio
 * @returns {Promise<string[]>} Lista de user IDs como strings
 */
export async function collectUserIdsForBuilding(buildingId) {
  const bid = buildingId == null ? NaN : Number(buildingId);
  if (!Number.isFinite(bid)) return [];

  const ids = new Set();
  const aptSnap = await getDocs(
    query(collection(db, "apartments"), where("building", "in", tolerantId(bid)))
  );
  aptSnap.docs.forEach((d) => {
    const { owner, tenant } = d.data();
    if (owner != null && owner !== "") ids.add(String(owner));
    if (tenant != null && tenant !== "") ids.add(String(tenant));
  });

  const ubSnap = await getDocs(
    query(collection(db, "userbuilding"), where("building", "in", tolerantId(bid)))
  );
  ubSnap.docs.forEach((d) => {
    const u = d.data().user;
    if (u != null && u !== "") ids.add(String(u));
  });

  return [...ids];
}

/**
 * Obtiene todos los usuarios vinculados a un edificio (owners, tenants,
 * userbuilding), con la consulta C10 de fetchUsersByUserIds.
 *
 * @param {string | number | null | undefined} buildingId - ID del edificio (obligatorio)
 * @returns {Promise<Array<import("../types/firestore").User & Record<string, any>>>} Usuarios del edificio
 * @throws {Error} sin un edificio válido; y si alguna consulta falla
 */
export async function fetchUsersForBuilding(buildingId) {
  filtroDelEdificio(buildingId, "fetchUsersForBuilding");
  const ids = await collectUserIdsForBuilding(buildingId);
  return fetchUsersByUserIds(ids, buildingId);
}

// Fase 0C: getNextAtomicId (contadores en `counters`) ya no está en el cliente.
// Los ids de identidad (userid, buildingid, occupancyId…) los reserva el
// servidor en functions/identity.
